import * as React from 'react'
import { Dialog, DialogProps } from 'primereact/dialog'
import { Button } from 'primereact/button'
import DOMPurify from 'dompurify'
import { replaceTextToHtmlTag } from '../utils/comUtil'

export type CommonDialogRef = {
    /**
     * 다이얼로그를 표시한다.
     */
    show(props: CommonDialogProps): void
    /**
     * 다이얼로그를 닫는다.
     */
    hide(): void
    /**
     * dialog 활성 여부
     */
    isOpen: boolean
}
export type CommonDialogProps = Omit<
    Partial<DialogProps>,
    'header' | 'footer'
> & {
    /**
     * 다이얼로그 제목
     */
    title?: React.ReactNode
    /**
     * 닫힐때 포커스를 리턴 받을 객체의 id
     */
    closeFocus?: string
    /**
     * 취소 버튼의 callback, 없으면 취소 버튼 보이지 않음.
     */
    reject?(): void
    /**
     * 취소 버튼 레이블, 기본값 '아니요'
     */
    rejectLabel?: string
    /**
     * 확인 버튼의 callback, 없으면 확인 버튼 보이지 않음.
     */
    accept?(): void
    /**
     * 확인 버튼 레이블, 기본값 '예'
     */
    acceptLabel?: string
}
export const CommonDialog = React.forwardRef<CommonDialogRef, {}>(
    (nullProps, ref) => {
        React.useImperativeHandle(
            ref,
            () => ({
                show(props: CommonDialogProps) {
                    props.visible = true
                    setProps(props)
                },

                hide() {
                    setProps(null)
                },
                isOpen: props?.visible ?? false,
            }),
            []
        )
        const [props, setProps] = React.useState<
            CommonDialogProps & { visible?: boolean }
        >(null)

        if (props === null) {
            return null
        }

        const {
            title,
            children,
            closeFocus = '',
            rejectLabel = '아니요',
            reject,
            acceptLabel = reject ? '예' : '확인',
            accept,
            onHide = () => {},
            ...rest
        } = props

        const footer = (
            <div className="buttons-x">
                {reject && (
                    <Button
                        label={rejectLabel}
                        className="button-outline-default -md"
                        onClick={() => {
                            setProps(null)
                            reject()
                        }}
                    />
                )}
                {accept && (
                    <Button
                        label={acceptLabel}
                        className="button-basic-primary -md"
                        onClick={() => {
                            setProps(null)
                            accept()
                        }}
                    />
                )}
            </div>
        )

        return (
            <Dialog
                {...rest}
                onHide={onHide}
                draggable={false}
                className={
                    `dialog-alert` +
                    `${rest.className ? ` ${rest.className}` : ''}`
                }
                transitionOptions={{ timeout: 0 }}
                footer={footer}
            >
                {title && <p className="h6">{title}</p>}

                {React.isValidElement(children) ? (
                    children
                ) : (
                    <div
                        dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(
                                replaceTextToHtmlTag(
                                    children?.toLocaleString()
                                ) ?? ''
                            ),
                        }}
                    ></div>
                )}
            </Dialog>
        )
    }
)
